import { Component, ErrorInfo, ReactNode } from 'react';
import styled from 'styled-components';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

const ErrorContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
  padding: 20px;
`;

const ErrorCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid #e5e7eb;
  max-width: 600px;
  width: 100%;
`;

const ErrorTitle = styled.h2`
  color: #dc2626;
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 16px;
`;

const ErrorMessage = styled.p`
  color: #6b7280;
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: 24px;
`;

const ErrorDetails = styled.details`
  margin-bottom: 24px;
  
  summary {
    cursor: pointer;
    color: #374151;
    font-weight: 500;
    margin-bottom: 8px;
  }
  
  pre {
    background: #f3f4f6;
    padding: 16px;
    border-radius: 8px;
    overflow-x: auto;
    font-size: 0.875rem;
    color: #374151;
  }
`;

const ReloadButton = styled.button`
  background: #667eea;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background: #5a67d8;
  }
`;

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({ error, errorInfo });
  }

  private handleReload = () => {
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      return (
        <ErrorContainer>
          <ErrorCard>
            <ErrorTitle>Qualcosa è andato storto</ErrorTitle>
            <ErrorMessage>
              Si è verificato un errore imprevisto nell'applicazione. Potrebbe trattarsi di un problema temporaneo.
              Prova a ricaricare la pagina.
            </ErrorMessage>
            
            {this.state.error && (
              <ErrorDetails>
                <summary>Dettagli Errore</summary>
                <pre>{this.state.error.toString()}</pre>
                {this.state.errorInfo && (
                  <pre>{this.state.errorInfo.componentStack}</pre>
                )}
              </ErrorDetails>
            )}
            
            <ReloadButton onClick={this.handleReload}>
              Ricarica Pagina
            </ReloadButton>
          </ErrorCard>
        </ErrorContainer>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;